import { createReducer, isAnyOf } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

import { SessionActions } from '@common/redux/actions/session';

const currentOrganizationSelector = createSelector(
  (state: ReadonlyArray<Organization>) => state,
  (organizations) => {
    return organizations.length > 0 ? organizations[0] : ({} as Organization);
  },
);

export interface SessionState {
  // from auth
  authenticated: boolean;
  user: AuthUser;
  role: MemberRole;
  isFetching: boolean;
  auto_pw?: boolean;
  is_social?: boolean;
  is_sso?: boolean;

  // from org
  isFetchingOrganizations: boolean;
  updateOrganizationName: {
    isFetching: boolean;
    error: string | null;
  };
  updateOrganizationSlugName: {
    isFetching: boolean;
    error: string | null;
    timestamp?: number;
  };
  samlConfigurationForm: {
    isFetching: boolean;
    isSaving: boolean;
    error: string | null;
    lastUpdatedTimestamp?: number;
  };
  currentOrganization: Organization;
}

const initialState: SessionState = {
  isFetching: false,
  authenticated: false,
  user: {
    country_code: '',
    email: '',
    new_email: '',
    email_verified: false,
    nickname: '',
    first_name: '',
    last_name: '',
    country_name: '',
    profile_id: 0,
    profile_url: '',
    user_id: 0,
    two_factor_authentication: false,
    verification_email_sent: false,
    coachmark_completed: false,
    join_date: '',
    account_locked: false,
    last_password_changed_at: '',
    is_onboarding_survey_done: false,
  },
  role: {
    name: 'GUEST',
    permissions: [],
    is_predefined: false,
    description: '',
    is_application_access_control: false,
    application_access_controls: [],
    application_access_controls_details: [],
    created_at: '',
    updated_at: '',
    id: 0,
    pii_access: 'none',
  },
  is_social: false,
  is_sso: false,

  isFetchingOrganizations: false,
  updateOrganizationName: {
    isFetching: false,
    error: null,
  },
  updateOrganizationSlugName: {
    isFetching: false,
    error: null,
  },
  samlConfigurationForm: {
    isFetching: false,
    isSaving: false,
    error: null,
  },
  currentOrganization: {} as Organization,
};

export const sessionReducer = createReducer<SessionState>(initialState, (builder) => {
  // AuthenticationActions
  builder
    .addCase(SessionActions.authFetchingSet, (state) => {
      state.isFetching = true;
    })
    .addCase(SessionActions.authFetchingUnset, (state) => {
      state.isFetching = false;
    })
    .addCase(SessionActions.verifyAuthentication.pending, (state) => {
      state.isFetching = true;
    })
    .addCase(SessionActions.authenticated, (state, { payload }) => {
      return {
        ...state,
        isFetching: false,
        authenticated: true,
        ...payload,
        currentOrganization: currentOrganizationSelector(payload.organizations),
      };
    })
    .addCase(SessionActions.signedIn, (state, { payload }) => {
      return {
        ...state,
        authenticated: !payload.two_factor_authentication_required,
        isFetching: false,
        ...payload,
      };
    })
    .addCase(SessionActions.verificationMailSent, (state) => {
      state.user.verification_email_sent = true;
    })
    .addCase(SessionActions.emailVerified, (state) => {
      state.user.email_verified = true;
      state.isFetching = false;
    })
    .addCase(SessionActions.accountActivated, (state) => {
      state.user.account_locked = false;
      state.isFetching = false;
    })
    .addCase(SessionActions.twoFactorAuthenticationSet, (state, { payload }) => {
      state.user.two_factor_authentication = payload.two_factor_authentication;
    })
    .addCase(SessionActions.unauthenticated, () => initialState);

  // OrganizationsActions
  builder
    .addCase(SessionActions.organizationCreated, (state, { payload }) => {
      state.currentOrganization = currentOrganizationSelector(payload.organizations);
      state.isFetchingOrganizations = false;
    })
    .addCase(SessionActions.organizationUpdated, (state, { payload }) => {
      state.isFetchingOrganizations = false;
      state.currentOrganization = {
        ...state.currentOrganization,
        ...payload,
      };
    })
    .addCase(SessionActions.updateOrganizationName.pending, (state) => {
      state.updateOrganizationName = {
        isFetching: true,
        error: null,
      };
    })
    .addCase(SessionActions.updateOrganizationName.fulfilled, (state) => {
      state.updateOrganizationName = {
        isFetching: false,
        error: null,
      };
    })
    .addCase(SessionActions.updateOrganizationName.rejected, (state, { payload }) => {
      state.updateOrganizationName = {
        isFetching: false,
        error: payload ?? null,
      };
    })
    .addCase(SessionActions.updateOrganizationSlugName.pending, (state) => {
      state.updateOrganizationSlugName.isFetching = true;
    })
    .addCase(SessionActions.updateOrganizationSlugName.fulfilled, (state, { payload }) => {
      state.currentOrganization = payload;
      state.updateOrganizationSlugName = {
        isFetching: false,
        error: null,
        timestamp: Date.now(),
      };
    })
    .addCase(SessionActions.updateOrganizationSlugName.rejected, (state, { payload }) => {
      state.updateOrganizationSlugName = {
        isFetching: false,
        error: payload ?? null,
      };
    })
    .addCase(SessionActions.updateSamlConfiguration.pending, (state) => {
      state.samlConfigurationForm.isFetching = false;
      state.samlConfigurationForm.isSaving = true;
    })
    .addCase(SessionActions.updateSamlConfiguration.fulfilled, (state, { payload }) => {
      const { sso_entity_id, sso_idp_url, sso_idp_cert, sso_enforcing, sso_jit_provisioning, sso_default_role } =
        payload;
      state.currentOrganization.sso_entity_id = sso_entity_id;
      state.currentOrganization.sso_idp_url = sso_idp_url;
      state.currentOrganization.sso_idp_cert = sso_idp_cert;
      state.currentOrganization.sso_enforcing = sso_enforcing;
      state.currentOrganization.sso_jit_provisioning = sso_jit_provisioning;
      state.currentOrganization.sso_default_role = sso_default_role;
      state.samlConfigurationForm = {
        isFetching: false,
        isSaving: false,
        error: null,
        lastUpdatedTimestamp: Date.now(),
      };
    })
    .addCase(SessionActions.updateSamlConfiguration.rejected, (state, { payload }) => {
      state.samlConfigurationForm = {
        isFetching: false,
        isSaving: false,
        error: payload ?? null,
      };
    })
    .addCase(SessionActions.updateSamlConfigurationReset, (state) => {
      state.samlConfigurationForm = initialState.samlConfigurationForm;
    })
    .addCase(SessionActions.userUpdated, (state, { payload }) => {
      state.user = payload;
    })
    .addCase(SessionActions.emailChanged, (state, { payload }) => {
      state.user.new_email = payload.new_email;
    })
    .addCase(SessionActions.passwordChanged, (state, { payload }) => {
      state.user = payload.user;
    });

  // AuthenticationActions
  builder.addMatcher(
    isAnyOf(SessionActions.verifyAuthentication.fulfilled, SessionActions.authenticationFailed),
    (state) => {
      state.isFetching = false;
    },
  );
});
